import { createFileRoute, Link } from "@tanstack/react-router";
import { breadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { faqPageJsonLd, type FaqItem } from "@/lib/faq-jsonld";
import { FaqAccordion } from "@/components/site/FaqAccordion";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ContactCta } from "@/components/site/ContactCta";
import offerteManImg from "@/assets/offerte-man-computer.png.asset.json";
import {
  Camera,
  Zap,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  FileText,
  CalendarCheck,
  HardHat,
  ShieldCheck,
  Clock,
  Wallet,
  Wrench,
  Phone,
  Mail,
  
  Receipt,
  Truck,
  BadgeCheck,
} from "lucide-react";

const SITE_URL = "https://elvion-group.nl";

export const Route = createFileRoute("/offerte/")({
  head: () => {
    const url = `${SITE_URL}/offerte`;
    const title = "Offerte opvragen | Camerabewaking & Groepenkast | Elvion Group Nederland";
    const description =
      "Stel zelf je offerte samen voor camerabewaking of een nieuwe groepenkast. All-inclusive door erkende monteurs. Gratis en vrijblijvend.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:locale", content: "nl_NL" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbJsonLd([{ name: "Offerte", path: "/offerte" }]),
        faqPageJsonLd(FAQ_ITEMS, `${SITE_URL}/offerte#faq`),
      ],
    };
  },
  component: OfferteHubPage,
});

const FAQ_ITEMS: FaqItem[] = [
  {
    q: "Wat kost een offerte bij Elvion Group Nederland?",
    a: "Een offerte is altijd gratis en volledig vrijblijvend. U betaalt pas als de opdracht is bevestigd en de installatie is uitgevoerd.",
  },
  {
    q: "Hoe snel ontvang ik mijn offerte?",
    a: "U ontvangt uw op maat gemaakte offerte doorgaans binnen 24 uur op werkdagen. Bij complexere projecten nemen wij eerst contact op om de wensen door te spreken.",
  },
  {
    q: "Kan ik zowel camerabewaking als een groepenkast in één offerte combineren?",
    a: "Ja, u kunt beide diensten combineren. Selecteer de configurators voor camerabewaking en groepenkasten of vermeld het in de toelichting — wij bundelen alles in één overzichtelijke offerte.",
  },
  {
    q: "Voor welke regio's kan ik een offerte aanvragen?",
    a: "Elvion Group Nederland werkt landelijk. Onze monteurs komen door heel Nederland, van Amsterdam en Rotterdam tot Den Haag, Utrecht, Eindhoven, Almere, Tilburg, Nijmegen en Arnhem.",
  },
  {
    q: "Zit installatie en montage inbegrepen in de offerteprijs?",
    a: "Ja, onze offertes zijn all-inclusive: advies, materiaal, montage en oplevering door onze eigen erkende monteurs zijn inbegrepen.",
  },
  {
    q: "Ben ik verplicht na een offerte een opdracht te geven?",
    a: "Nee, een offerte is volledig vrijblijvend. U beslist zelf of u de opdracht wilt bevestigen — er zit geen enkele verplichting aan vast.",
  },
];

const PRODUCTS = [
  {
    icon: Camera,
    title: "Camerabewaking",
    desc: "Stel zelf je camerasysteem samen: vorm, aantal, kwaliteit en opslag. Inclusief montage door erkende monteurs.",
    href: "/offerte-aanvragen", product: "camerabewaking",
    bullets: [
      "Dome, Turret, Bullet of PTZ",
      "Full HD t/m 4K met AcuSense/WizSense",
      "Inclusief recorder, kabels & installatie",
    ],
  },
  {
    icon: Zap,
    title: "Groepenkast",
    desc: "Configureer een nieuwe of vervangende groepenkast. Van 1-fase 8-groeps tot 3-fase met laadgroep type B.",
    href: "/offerte-aanvragen", product: "groepenkast",
    bullets: [
      "1-fase of 3-fase, altijd AEG",
      "Aardlekautomaten type A & B",
      "Stuklijst, prijsindicatie & NEN 1010",
    ],
  },
];

const PROMISES = [
  "Gratis en vrijblijvend",
  "Reactie binnen 1 werkdag",
  "All-inclusive: materiaal + installatie",
  "Erkende & verzekerde monteurs",
];

const STEPS = [
  {
    icon: ClipboardList,
    title: "1. Stel samen",
    body: "Doorloop de configurator en kies het systeem of de kast die bij je situatie past.",
  },
  {
    icon: Receipt,
    title: "2. Direct prijsindicatie",
    body: "Je ziet meteen een realistische richtprijs, inclusief materiaal en montage.",
  },
  {
    icon: FileText,
    title: "3. Offerte op maat",
    body: "Binnen één werkdag ontvang je een transparante offerte met stuklijst en planning.",
  },
  {
    icon: HardHat,
    title: "4. Vakkundige uitvoering",
    body: "Akkoord? Onze monteurs plannen de installatie netjes en NEN 1010 conform in.",
  },
];

const WHY = [
  {
    icon: Wallet,
    title: "Transparante prijzen",
    body: "Geen verrassingen achteraf. Alles staat netjes uitgesplitst in je offerte.",
  },
  {
    icon: BadgeCheck,
    title: "A-merken uit voorraad",
    body: "Hikvision, Dahua en AEG, direct leverbaar uit onze eigen voorraad.",
  },
  {
    icon: ShieldCheck,
    title: "Erkende installateurs",
    body: "Werkzaamheden volgens NEN 1010, met garantie en verzekering.",
  },
  {
    icon: Clock,
    title: "Snelle planning",
    body: "Vaak binnen één tot twee weken ingepland, ook avond/weekend mogelijk.",
  },
  {
    icon: Truck,
    title: "Landelijk werkgebied",
    body: "Particulier, MKB en winkelketens, door heel Nederland inzetbaar.",
  },
  {
    icon: Wrench,
    title: "Service na oplevering",
    body: "Onderhoud, support en uitbreidingen door dezelfde vaste monteurs.",
  },
];

const FAQS = [
  {
    q: "Is een offerte echt gratis en vrijblijvend?",
    a: "Ja. Je betaalt pas zodra je akkoord geeft op de definitieve offerte en de opdracht aan ons gunt. Aan het configureren of opvragen zitten geen kosten.",
  },
  {
    q: "Hoe snel ontvang ik mijn offerte?",
    a: "Na het versturen van je configuratie ontvang je binnen één werkdag een persoonlijke offerte. Bij spoed bellen we dezelfde dag nog terug.",
  },
  {
    q: "Wat zit er allemaal bij de prijs inbegrepen?",
    a: "Alle materialen (camera's, recorder, groepenkast, automaten, kabels, bevestiging), arbeid, voorrijden en oplevering. Je weet vooraf precies wat je betaalt.",
  },
  {
    q: "Kan ik nog wijzigen na het aanvragen?",
    a: "Zeker. Onze adviseur neemt de configuratie met je door en past het systeem of de kast aan op jouw specifieke situatie voordat we de definitieve offerte opmaken.",
  },
  {
    q: "Werken jullie ook voor bedrijven en winkels?",
    a: "Ja. Naast particulieren bedienen we MKB, horeca, winkels en winkelketens, inclusief winkelrenovaties en projectplanning buiten openingstijden.",
  },
];

const CHANNELS = [
  {
    icon: Phone,
    title: "Telefonisch",
    desc: "Direct contact met een adviseur",
    label: "085 080 66 95",
    href: "tel:0850806695",
  },
  {
    icon: Mail,
    title: "E-mail",
    desc: "Stuur je vraag of bestaande situatie",
    label: "info@elvion-group.nl",
    href: "mailto:info@elvion-group.nl",
  },
];

function OfferteHubPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header variant="shop" />
      <main className="flex-1">
        <ContactCta
          imageUrl={offerteManImg.url}
          imageAlt="Klant vraagt online een offerte aan voor camerabewaking"
          imageLeft
          title="Direct een offerte opvragen?"
          description="Stel zelf je systeem samen of laat onze specialisten een passende offerte op maat maken. Gratis, vrijblijvend en binnen één werkdag in je inbox."
          bullets={[
            "Persoonlijke offerte binnen 1 werkdag",
            "All-inclusive: materiaal én installatie",
            "Transparante prijzen, geen verrassingen",
          ]}
          primaryCta={{ label: "Offerte opvragen", to: "/offerte-aanvragen" }}
          secondaryCta={{ label: "Neem contact op", to: "/klantenservice" }}
        />




        {/* Hero */}
        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
                Offerte opvragen: eenvoudig en transparant
              </h1>
              <p className="mt-4 max-w-xl text-base text-text-secondary sm:text-lg">
                Configureer in een paar stappen je camerasysteem of groepenkast.
                Je ziet direct een prijsindicatie en ontvangt binnen één werkdag
                een persoonlijke, all-inclusive offerte, materiaal en
                installatie inbegrepen.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/offerte-aanvragen" search={{ product: "camerabewaking" }}
                  className="inline-flex items-center gap-2 rounded-md bg-orange px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange/90"
                >
                  Camerasysteem samenstellen
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/offerte-aanvragen" search={{ product: "groepenkast" }}
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-orange hover:text-orange"
                >
                  Groepenkast samenstellen
                </Link>
              </div>
              <ul className="mt-6 grid gap-2 text-sm text-text-secondary sm:grid-cols-2">
                {PROMISES.map((p) => (
                  <li key={p} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-orange" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-orange/20 via-orange/5 to-transparent blur-2xl" />
              <div className="relative rounded-2xl border border-border bg-card p-8 shadow-lg">
                <h2 className="font-display text-lg font-bold text-foreground">
                  Liever direct contact?
                </h2>
                <p className="mt-1 text-sm text-text-secondary">
                  Kies de manier die je het beste uitkomt, we reageren snel.
                </p>
                <div className="mt-5 space-y-3">
                  {CHANNELS.map((c) => (
                    <div
                      key={c.title}
                      className="flex items-start gap-4 rounded-xl border border-border bg-background p-4"
                    >
                      <div className="rounded-full bg-orange/10 p-2.5 text-orange">
                        <c.icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-foreground">{c.title}</p>
                        <p className="text-xs text-text-secondary">{c.desc}</p>
                        {c.href ? (
                          <a
                            href={c.href}
                            className="mt-1 inline-block text-sm font-semibold text-orange hover:underline"
                          >
                            {c.label}
                          </a>
                        ) : (
                          <span className="mt-1 inline-block text-sm font-medium text-foreground/70">
                            {c.label}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Product configurators */}
        <section className="border-t border-border bg-card/30">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                Waar wil je een offerte voor?
              </h2>
              <p className="mt-3 text-base text-text-secondary">
                Kies een product en stel jouw configuratie samen. Andere wens?
                Vraag dan een persoonlijk advies aan, we helpen je graag verder.
              </p>
            </div>

            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {PRODUCTS.map((p) => (
                <Link
                  key={p.title}
                  to={p.href} search={{ product: p.product }}
                  className="group rounded-2xl border border-border bg-card p-8 shadow-sm transition-all hover:border-orange/50 hover:shadow-md"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange/10 text-orange">
                    <p.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-4 text-2xl font-bold text-foreground group-hover:text-orange">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-text-secondary">{p.desc}</p>
                  <ul className="mt-5 space-y-2">
                    {p.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-foreground">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-orange">
                    Start configurator
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>

            <div className="mt-8 rounded-xl border border-dashed border-border bg-background p-6 text-center">
              <p className="text-sm text-text-secondary">
                Iets anders nodig? Denk aan laadpalen, een uitbreiding, tuinverlichting,
                een winkelrenovatie of advies bij 1-fase naar 3-fase.
              </p>
              <Link
                to="/advies"
                className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-orange hover:underline"
              >
                Vraag persoonlijk advies aan
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              Zo werkt het in 4 stappen
            </h2>
            <p className="mt-3 text-base text-text-secondary">
              Van eerste configuratie tot vakkundige oplevering. Duidelijk,
              snel en zonder verrassingen.
            </p>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s) => (
              <div
                key={s.title}
                className="rounded-2xl border border-border bg-card p-6 shadow-sm"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange/10 text-orange">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-lg font-bold text-foreground">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-text-secondary">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why us */}
        <section className="border-t border-border bg-card/30">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                Waarom een offerte bij Elvion Group Nederland?
              </h2>
              <p className="mt-3 text-base text-text-secondary">
                Geen verkooppraat, geen verborgen kosten. Wel een eerlijk advies
                en vakwerk dat jaren meegaat.
              </p>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {WHY.map((w) => (
                <div
                  key={w.title}
                  className="rounded-2xl border border-border bg-card p-6 shadow-sm"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange/10 text-orange">
                    <w.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold text-foreground">
                    {w.title}
                  </h3>
                  <p className="mt-2 text-sm text-text-secondary">{w.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              Veelgestelde vragen
            </h2>
            <p className="mt-3 text-base text-text-secondary">
              Geen antwoord op je vraag? Bel of mail ons, we helpen je graag verder.
            </p>
          </div>

          <div className="mt-10 divide-y divide-border rounded-2xl border border-border bg-card">
            {FAQS.map((f) => (
              <details
                key={f.q}
                className="group px-6 py-5 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-4">
                  <span className="font-display text-base font-semibold text-foreground">
                    {f.q}
                  </span>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border text-orange transition-transform group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm text-text-secondary">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <ContactCta
          title="Nog vragen over uw offerte?"
          description="Twijfelt u over configuratie, materiaalkeuze of planning? Onze offerte-specialisten denken graag mee en zorgen dat uw aanvraag helemaal aansluit op uw project."
        />

        {/* Final CTA */}
        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-6 rounded-2xl bg-primary p-8 text-primary-foreground sm:flex-row sm:p-10">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/10">
                <CalendarCheck className="size-6 text-white" />
              </div>
              <div>
                <h3 className="font-display text-xl font-bold">Klaar om te starten?</h3>
                <p className="mt-1 text-white/80">
                  Stel je configuratie samen of bel direct met een adviseur.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/offerte-aanvragen" search={{ product: "camerabewaking" }}
                className="inline-flex items-center gap-2 rounded-lg bg-orange px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-orange/90"
              >
                Start configurator
                <ArrowRight className="size-4" />
              </Link>
              <a
                href="tel:0850806695"
                className="inline-flex items-center gap-2 rounded-lg border border-white/30 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                <Phone className="size-4" />
                085 080 66 95
              </a>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FaqAccordion
            id="offerte-faq"
            title="Veelgestelde vragen over offertes"
            intro="Antwoorden op de meest gestelde vragen over onze offerte-aanvraag, prijzen en werkwijze."
            items={FAQ_ITEMS}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}
